import { useState, useEffect } from "react";
import { useIsDevEnvironment } from "@/hooks/useIsDevEnvironment";

export const HACKLE_VARIATIONS = ["A", "B", "C", "D", "Z"] as const;
export const HACKLE_DEVTOOLS_STORAGE_KEY = "local_test_variations";
export const REGISTERED_EXPERIMENTS_KEY = "local_registered_experiments";

type VariationMap = Record<number, string>;

export const registerExperiment = (experimentKey: number) => {
  if (typeof window === "undefined") return;

  const stored = window.localStorage.getItem(REGISTERED_EXPERIMENTS_KEY);
  const experiments: number[] = stored ? JSON.parse(stored) : [];

  if (!experiments.includes(experimentKey)) {
    experiments.push(experimentKey);
    window.localStorage.setItem(
      REGISTERED_EXPERIMENTS_KEY,
      JSON.stringify(experiments)
    );
    window.dispatchEvent(new Event("hackle-experiment-registered"));
  }
};

export const getRegisteredExperiments = (): number[] => {
  if (typeof window === "undefined") return [];
  const stored = window.localStorage.getItem(REGISTERED_EXPERIMENTS_KEY);
  return stored ? JSON.parse(stored) : [];
};

export const GoiDevTools = () => {
  const isDevEnvironment = useIsDevEnvironment();

  if (isDevEnvironment === true) {
    return <GoiHackleDevtools />;
  }

  return <></>;
};

const GoiHackleDevtools = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [variations, setVariations] = useState<VariationMap>({});
  const [registeredExperiments, setRegisteredExperiments] = useState<number[]>(
    []
  );
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    const stored = window.localStorage.getItem(HACKLE_DEVTOOLS_STORAGE_KEY);
    if (stored) {
      try {
        setVariations(JSON.parse(stored));
      } catch {
        setVariations({});
      }
    }

    setRegisteredExperiments(getRegisteredExperiments());

    const handleNewExperiment = () => {
      setRegisteredExperiments(getRegisteredExperiments());
    };

    window.addEventListener(
      "hackle-experiment-registered",
      handleNewExperiment
    );

    return () => {
      window.removeEventListener(
        "hackle-experiment-registered",
        handleNewExperiment
      );
    };
  }, []);

  const saveVariations = (newVariations: VariationMap) => {
    window.localStorage.setItem(
      HACKLE_DEVTOOLS_STORAGE_KEY,
      JSON.stringify(newVariations)
    );
    setVariations(newVariations);
    setHasChanges(true);
  };

  const handleVariationChange = (experimentKey: number, variation: string) => {
    const newVariations = { ...variations, [experimentKey]: variation };
    saveVariations(newVariations);
  };

  const handleClearExperiment = (experimentKey: number) => {
    const newVariations = Object.fromEntries(
      Object.entries(variations).filter(
        ([key]) => Number(key) !== experimentKey
      )
    );
    saveVariations(newVariations);
  };

  const handleClearAll = () => {
    window.localStorage.removeItem(HACKLE_DEVTOOLS_STORAGE_KEY);
    window.localStorage.removeItem(REGISTERED_EXPERIMENTS_KEY);
    setVariations({});
    setRegisteredExperiments([]);
    setHasChanges(true);
  };

  const handleReload = () => {
    window.location.reload();
  };

  const handleRefreshExperiments = () => {
    setRegisteredExperiments(getRegisteredExperiments());
    alert("새로고침 완료");
  };

  return (
    <>
      {isOpen ? (
        <div
          style={{
            position: "fixed",
            top: "50%",
            right: 16,
            transform: "translateY(-50%)",
            zIndex: 9999,
            background: "#1a1a1a",
            borderRadius: 12,
            boxShadow: "0 8px 32px rgba(0,0,0,0.4), 0 2px 8px rgba(0,0,0,0.3)",
            width: 300,
            maxHeight: "60vh",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: 16,
              borderBottom: "1px solid #333",
              flexShrink: 0,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ color: "#fff", fontWeight: 600, fontSize: 14 }}>
                🧪 Hackle Devtools
              </span>
              <button
                onClick={handleRefreshExperiments}
                style={{
                  background: "none",
                  border: "none",
                  color: "#4ade80",
                  cursor: "pointer",
                  fontSize: 16,
                }}
                title="실험 목록 새로고침"
              >
                <u>새로고침</u>
              </button>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              style={{
                background: "none",
                border: "none",
                color: "#888",
                cursor: "pointer",
                fontSize: 18,
              }}
            >
              ×
            </button>
          </div>

          <div
            style={{
              color: "white",
              padding: "0px 16px 8px 16px",
              fontSize: 12,
            }}
          >
            실험이 보이지 않는 경우 &quot;새로고침&quot; 버튼을 눌러주세요
          </div>
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              padding: 16,
            }}
          >
            {hasChanges && (
              <div
                style={{
                  background: "#fbbf24",
                  color: "#000",
                  padding: "8px 12px",
                  borderRadius: 6,
                  marginBottom: 12,
                  fontSize: 13,
                  fontWeight: 500,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <span>⚠️</span>
                <span>
                  변경사항을 적용하려면 &quot;적용하기&quot;를 눌러주세요!
                </span>
              </div>
            )}

            {registeredExperiments.length === 0 ? (
              <div
                style={{
                  color: "#666",
                  fontSize: 13,
                  textAlign: "center",
                  padding: 16,
                }}
              >
                No experiments detected yet.
                <br />
                <span style={{ fontSize: 11 }}>
                  Navigate to pages using useCustomVariation
                </span>
              </div>
            ) : (
              <div
                style={{ display: "flex", flexDirection: "column", gap: 12 }}
              >
                {registeredExperiments.map((experimentKey) => (
                  <div
                    key={experimentKey}
                    style={{
                      background: "#2a2a2a",
                      borderRadius: 6,
                      padding: 12,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 8,
                      }}
                    >
                      <span
                        style={{
                          color: "#4ade80",
                          fontWeight: 600,
                          fontSize: 13,
                        }}
                      >
                        Exp #{experimentKey}
                      </span>
                      {variations[experimentKey] && (
                        <button
                          onClick={() => handleClearExperiment(experimentKey)}
                          style={{
                            background: "none",
                            border: "none",
                            color: "#f87171",
                            cursor: "pointer",
                            fontSize: 12,
                          }}
                        >
                          Reset
                        </button>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: 4 }}>
                      {HACKLE_VARIATIONS.map((v) => (
                        <button
                          key={v}
                          onClick={() =>
                            handleVariationChange(experimentKey, v)
                          }
                          style={{
                            flex: 1,
                            padding: "6px 8px",
                            border: "none",
                            borderRadius: 4,
                            cursor: "pointer",
                            background:
                              variations[experimentKey] === v
                                ? "#4ade80"
                                : "#444",
                            color:
                              variations[experimentKey] === v ? "#000" : "#fff",
                            fontWeight: 500,
                            fontSize: 12,
                          }}
                        >
                          {v}
                        </button>
                      ))}
                    </div>
                    {!variations[experimentKey] && (
                      <div
                        style={{ color: "#888", fontSize: 11, marginTop: 6 }}
                      >
                        Using SDK default
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div
            style={{
              display: "flex",
              gap: 8,
              padding: 16,
              borderTop: "1px solid #333",
              background: "#1a1a1a",
              flexShrink: 0,
            }}
          >
            <button
              onClick={handleClearAll}
              style={{
                flex: 1,
                padding: "10px 12px",
                border: "1px solid #555",
                borderRadius: 6,
                cursor: "pointer",
                background: "transparent",
                color: "#888",
                fontWeight: 500,
              }}
            >
              전체 초기화
            </button>
            <button
              onClick={handleReload}
              style={{
                flex: 1,
                padding: "10px 12px",
                border: "none",
                borderRadius: 6,
                cursor: "pointer",
                background: hasChanges ? "#f59e0b" : "#3b82f6",
                color: "#fff",
                fontWeight: 600,
                animation: hasChanges ? "pulse 1.5s infinite" : "none",
              }}
            >
              {hasChanges ? "🔄 적용하기" : "적용하기"}
            </button>
          </div>

          <style>
            {`
              @keyframes pulse {
                0%, 100% { opacity: 1; }
                50% { opacity: 0.7; }
              }
            `}
          </style>
        </div>
      ) : (
        <button
          onClick={() => setIsOpen(true)}
          style={{
            position: "fixed",
            bottom: "30%",
            right: 16,
            zIndex: 9999,
            width: 48,
            height: 48,
            borderRadius: "50%",
            background: hasChanges ? "#f59e0b" : "#7860ff",
            border: "none",
            cursor: "pointer",
            fontSize: 20,
            boxShadow: "0 4px 16px rgba(0,0,0,0.3), 0 2px 4px rgba(0,0,0,0.2)",
            animation: hasChanges ? "pulse 1.5s infinite" : "none",
          }}
        >
          {hasChanges ? "🔄" : "🧪"}
        </button>
      )}

      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.7; }
          }
        `}
      </style>
    </>
  );
};
